import React, { useState } from 'react';
import {
  useDeleteCoefficientTableMutation,
  useGetCoefficientTables,
  useSaveCoefficientTableMutation,
} from '../api/react-query';
import { buildCoefficientTablePayload } from '../api/transformers';
import {
  SectionHeader,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  FormField,
  FormGrid,
  FormActions,
  Button,
  Input,
  Select,
  EmptyState,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../shared/ui/composites';
import { fmtInt, fmtNum } from '../../../shared/utils/formatters';

const cardGrid = 'grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4';

const checkboxLabel =
  'inline-flex items-center gap-1.5 border border-[#e2e8f0] rounded-full px-3 py-1.5';

// Additional coefficient fields for complex calculations
const COEFFICIENT_FIELDS = {
  // Basic 4 coefficients
  a: { label: 'ضریب a', type: 'number' },
  b: { label: 'ضریب b', type: 'number' },
  c: { label: 'ضریب c', type: 'number' },
  d: { label: 'ضریب d', type: 'number' },
  
  // Role-based percentages (for hierarchical commissions)
  seller_pct: { label: 'درصد فروشنده', type: 'percentage' },
  supervisor_pct: { label: 'درصد سرپرست', type: 'percentage' },
  manager_pct: { label: 'درصد مدیر شعبه', type: 'percentage' },
  sales_manager_pct: { label: 'درصد رئیس فروش', type: 'percentage' },
  
  // Achievement-based (performance tiers)
  achievement_min: { label: 'حداقل دستیابی %', type: 'percentage' },
  achievement_max: { label: 'حداکثر دستیابی %', type: 'percentage' },
  base_commission: { label: 'پورسانت پایه', type: 'number' },
  bonus_rate: { label: 'نرخ پاداش', type: 'number' },
};

const DEALER_TYPE_OPTIONS = [
  'عادی', 'VIP', 'ویژه', 'A+', 'فروشنده', 'سرپرست', 'مدیر', 'رئیس فروش',
];

const CATEGORY_OPTIONS = [
  'عادی', 'عمده', 'ویژه', 'تلفیقی', 'تهران', 'بوشهر', 'خوزستان',
  'شیراز', 'اصفهان', 'یزد', 'جنوب عادی', 'پونک', 'گیجه/بازل', 'زعفران', 'سایر',
];

function CoefficientsTab() {
  const { data: tables = [], refetch: reloadTables } = useGetCoefficientTables();
  const saveTable = useSaveCoefficientTableMutation();
  const deleteTable = useDeleteCoefficientTableMutation();

  const [showEditor, setShowEditor] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [tableName, setTableName] = useState('');
  const [tableDescription, setTableDescription] = useState('');
  const [tableRows, setTableRows] = useState([]);
  const [tableType, setTableType] = useState('basic');
  const [selectedFields, setSelectedFields] = useState(['a', 'b', 'c', 'd']);

  const handleCreateNew = () => {
    setEditingTable(null);
    setTableName('');
    setTableDescription('');
    setTableType('basic');
    setSelectedFields(['a', 'b', 'c', 'd']);
    setTableRows([
      { dealer_type: 'عادی', category: 'عادی', a: 0.0025, b: 0.005, c: 0.015, d: 0.025 }
    ]);
    setShowEditor(true);
  };

  const handleEditTable = (table) => {
    setEditingTable(table);
    setTableName(table.name);
    setTableDescription(table.description || '');
    setTableType(table.table_type || 'basic');
    setSelectedFields(table.selected_fields || ['a', 'b', 'c', 'd']);
    setTableRows([...table.rows]);
    setShowEditor(true);
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('آیا از حذف این جدول ضرایب اطمینان دارید؟')) {
      return;
    }

    try {
      await deleteTable.mutateAsync(tableId);
      alert('جدول با موفقیت حذف شد');
      reloadTables();
    } catch (error) {
      console.error('Error deleting table:', error);
      alert('خطا در حذف جدول');
    }
  };

  const handleAddRow = () => {
    const newRow = { dealer_type: 'عادی', category: 'عادی' };
    selectedFields.forEach(field => {
      newRow[field] = 0;
    });
    setTableRows([...tableRows, newRow]);
  };

  const handleRemoveRow = (index) => {
    setTableRows(tableRows.filter((_, i) => i !== index));
  };

  const handleRowChange = (index, field, value) => {
    const newRows = [...tableRows];
    if (field === 'dealer_type' || field === 'category') {
      newRows[index][field] = value;
    } else {
      newRows[index][field] = parseFloat(value) || 0;
    }
    setTableRows(newRows);
  };

  const handleTableTypeChange = (type) => {
    setTableType(type);
    
    // Set default fields based on table type
    if (type === 'basic') {
      setSelectedFields(['a', 'b', 'c', 'd']);
    } else if (type === 'hierarchical') {
      setSelectedFields(['seller_pct', 'supervisor_pct', 'manager_pct', 'sales_manager_pct']);
    } else if (type === 'performance') {
      setSelectedFields(['achievement_min', 'achievement_max', 'base_commission', 'bonus_rate']);
    }
    
    // Update existing rows with new fields
    const newRows = tableRows.map(row => {
      const newRow = { dealer_type: row.dealer_type, category: row.category };
      if (type === 'basic') {
        newRow.a = row.a || 0;
        newRow.b = row.b || 0;
        newRow.c = row.c || 0;
        newRow.d = row.d || 0;
      } else if (type === 'hierarchical') {
        newRow.seller_pct = row.seller_pct || 0;
        newRow.supervisor_pct = row.supervisor_pct || 0;
        newRow.manager_pct = row.manager_pct || 0;
        newRow.sales_manager_pct = row.sales_manager_pct || 0;
      } else if (type === 'performance') {
        newRow.achievement_min = row.achievement_min || 0;
        newRow.achievement_max = row.achievement_max || 100;
        newRow.base_commission = row.base_commission || 0;
        newRow.bonus_rate = row.bonus_rate || 0;
      }
      return newRow;
    });
    setTableRows(newRows);
  };

  const handleFieldToggle = (field) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter(f => f !== field));
    } else {
      setSelectedFields([...selectedFields, field]);
    }
  };

  const handleSave = async () => {
    if (!tableName.trim()) {
      alert('لطفاً نام جدول را وارد کنید');
      return;
    }

    if (tableRows.length === 0) {
      alert('لطفاً حداقل یک ردیف به جدول اضافه کنید');
      return;
    }

    const tableData = buildCoefficientTablePayload({
      id: editingTable?.id || Date.now().toString(),
      name: tableName,
      description: tableDescription,
      table_type: tableType,
      selected_fields: selectedFields,
      rows: tableRows,
      created_at: editingTable?.created_at,
    });

    try {
      await saveTable.mutateAsync(tableData);
      alert('جدول با موفقیت ذخیره شد');
      reloadTables();
      handleCancel();
    } catch (error) {
      console.error('Error saving table:', error);
      alert('خطا در ذخیره جدول');
    }
  };

  const handleCancel = () => {
    setEditingTable(null);
    setTableName('');
    setTableDescription('');
    setTableType('basic');
    setSelectedFields(['a', 'b', 'c', 'd']);
    setTableRows([]);
    setShowEditor(false);
  };

  return (
    <div className="w-full min-w-0">
      {!showEditor ? (
        <div className="w-full min-w-0">
          <SectionHeader
            title="جداول ضرایب"
            action={
              <Button variant="add" onClick={handleCreateNew}>
                ایجاد جدول جدید
              </Button>
            }
          />

          <div className={cardGrid}>
            {tables.length === 0 ? (
              <EmptyState
                className="col-span-full"
                message="هیچ جدول ضریبی تعریف نشده است"
                actionLabel="ایجاد اولین جدول"
                onAction={handleCreateNew}
              />
            ) : (
              tables.map(table => (
                <Card key={table.id} variant="panel">
                  <CardHeader>
                    <CardTitle>{table.name}</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="icon"
                        size="icon"
                        onClick={() => handleEditTable(table)}
                        title="ویرایش"
                      >
                        ✏️
                      </Button>
                      <Button
                        variant="icon"
                        size="icon"
                        onClick={() => handleDeleteTable(table.id)}
                        title="حذف"
                      >
                        🗑️
                      </Button>
                    </div>
                  </CardHeader>
                  {table.description && (
                    <CardDescription>{table.description}</CardDescription>
                  )}
                  <div className="overflow-x-auto">
                    <Table className="min-w-[620px]">
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead>نوع نماینده</TableHead>
                          <TableHead>دسته</TableHead>
                          <TableHead>a</TableHead>
                          <TableHead>b</TableHead>
                          <TableHead>c</TableHead>
                          <TableHead>d</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {table.rows.slice(0, 3).map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{row.dealer_type}</TableCell>
                            <TableCell>{row.category}</TableCell>
                            <TableCell>{fmtNum(row.a)}</TableCell>
                            <TableCell>{fmtNum(row.b)}</TableCell>
                            <TableCell>{fmtNum(row.c)}</TableCell>
                            <TableCell>{fmtNum(row.d)}</TableCell>
                          </TableRow>
                        ))}
                        {table.rows.length > 3 && (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              className="text-center text-[#999] text-xs"
                            >
                              و {fmtInt(table.rows.length - 3)} ردیف دیگر...
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="w-full min-w-0">
          <SectionHeader
            title={editingTable ? 'ویرایش جدول ضرایب' : 'ایجاد جدول ضرایب جدید'}
            action={
              <Button variant="cancel" onClick={handleCancel}>
                بازگشت
              </Button>
            }
          />

          <div className="flex flex-col gap-4">
            <Card variant="panel">
              <FormGrid className="mb-0">
                <FormField label="نام جدول" required>
                  <Input
                    variant="form"
                    type="text"
                    value={tableName}
                    onChange={(e) => setTableName(e.target.value)}
                    placeholder="مثال: ضرایب پورسانت 1404"
                  />
                </FormField>
                <FormField label="توضیحات">
                  <Input
                    variant="form"
                    type="text"
                    value={tableDescription}
                    onChange={(e) => setTableDescription(e.target.value)}
                    placeholder="توضیحات اختیاری"
                  />
                </FormField>
                <FormField label="نوع جدول" required>
                  <Select
                    value={tableType}
                    onChange={(e) => handleTableTypeChange(e.target.value)}
                  >
                    <option value="basic">پایه (a, b, c, d)</option>
                    <option value="hierarchical">سلسله مراتبی (فروشنده، سرپرست، مدیر)</option>
                    <option value="performance">عملکردی (بازه دستیابی)</option>
                    <option value="custom">سفارشی (انتخاب فیلدها)</option>
                  </Select>
                </FormField>
                {tableType === 'custom' && (
                  <FormField label="فیلدهای جدول">
                    <div className="flex flex-wrap items-center gap-2">
                      {Object.entries(COEFFICIENT_FIELDS).map(([key, field]) => (
                        <label key={key} className={checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={selectedFields.includes(key)}
                            onChange={() => handleFieldToggle(key)}
                          />
                          {field.label}
                        </label>
                      ))}
                    </div>
                  </FormField>
                )}
              </FormGrid>
            </Card>

            <Card variant="panel">
              <CardHeader className="mb-4">
                <CardTitle>ضرایب ({fmtInt(tableRows.length)} ردیف)</CardTitle>
                <Button variant="add" onClick={handleAddRow}>
                  + افزودن ردیف
                </Button>
              </CardHeader>
              <Table className="min-w-[620px]">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[150px]">نوع نماینده</TableHead>
                    <TableHead className="w-[150px]">دسته‌بندی</TableHead>
                    {selectedFields.map(field => (
                      <TableHead key={field} className="w-[120px]">
                        {COEFFICIENT_FIELDS[field]?.label || field}
                      </TableHead>
                    ))}
                    <TableHead className="w-[80px]">عملیات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableRows.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Select
                          className="min-w-[90px] px-2 py-1.5"
                          value={row.dealer_type}
                          onChange={(e) => handleRowChange(index, 'dealer_type', e.target.value)}
                        >
                          {DEALER_TYPE_OPTIONS.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          className="min-w-[90px] px-2 py-1.5"
                          value={row.category}
                          onChange={(e) => handleRowChange(index, 'category', e.target.value)}
                        >
                          {CATEGORY_OPTIONS.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </Select>
                      </TableCell>
                      {selectedFields.map(field => (
                        <TableCell key={field}>
                          <Input
                            variant="form"
                            type="number"
                            className="min-w-[90px] px-2 py-1.5"
                            step={COEFFICIENT_FIELDS[field]?.type === 'percentage' ? '1' : '0.0001'}
                            value={row[field] || 0}
                            onChange={(e) => handleRowChange(index, field, e.target.value)}
                            placeholder={COEFFICIENT_FIELDS[field]?.type === 'percentage' ? '%' : ''}
                          />
                        </TableCell>
                      ))}
                      <TableCell>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleRemoveRow(index)}
                          title="حذف ردیف"
                        >
                          🗑️
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            <FormActions>
              <Button variant="submit" onClick={handleSave}>
                ذخیره جدول
              </Button>
              <Button variant="cancel" onClick={handleCancel}>
                انصراف
              </Button>
            </FormActions>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoefficientsTab;
